import { useState, useEffect, useMemo, useCallback } from "react";
import { parseCharacters, generateFieldId } from "@/utils/format";
import { getFieldConfiguration } from "../config/fieldConfigurations";
import type { FieldConfig } from "../config/type";
import type { EntityField } from "@/pages/digital-files/types";
import type {
  UseFieldFormProps,
  UseFieldFormReturn,
} from "./useFieldForm.types";
import {
  buildInitialDynamicValues as buildInitialDynamicValuesImpl,
  setupDatacenterFields as setupDatacenterFieldsImpl,
  setupFileTypeConfiguration as setupFileTypeConfigurationImpl,
  setupOptionsRootConfiguration as setupOptionsRootConfigurationImpl,
  setupDropdownButtonConfiguration as setupDropdownButtonConfigurationImpl,
  setupIdentificationField as setupIdentificationFieldImpl,
  setupSourceConfiguration as setupSourceConfigurationImpl,
  hasRequiredValidationError as hasRequiredValidationErrorImpl,
  manageFieldValidation as manageFieldValidationImpl,
  buildFieldData,
} from "./useFieldForm.helpers";

export const useFieldForm = ({
  type,
  initialField,
  isOpen,
}: UseFieldFormProps): UseFieldFormReturn => {
  // Estados del formulario
  const [fieldId, setFieldId] = useState("");
  const [fieldName, setFieldNameState] = useState(""); // NOSONAR: is intended to be separate from setFieldName which has validation logic
  const [keyValue, setKeyValue] = useState("");
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [localListItems, setLocalListItems] = useState<string[]>([]);
  const [newListItem, setNewListItem] = useState("");
  const [newFileType, setNewFileType] = useState("");
  const [locked, setLocked] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // is array
  const [isArray, setIsArray] = useState(false);
  const [allowedQuantityOfValues, setAllowedQuantityOfValues] = useState("");

  // Wrapper para setFieldName que valida en tiempo real
  const setFieldName = (value: string) => {
    setFieldNameState(value);

    // Validar en tiempo real si el nombre es "ID"
    if (value.trim().toUpperCase() === "ID") {
      setErrors((prev) => ({
        ...prev,
        fieldName: "No se puede crear un campo con el nombre 'ID'",
      }));
    } else {
      // Limpiar el error si existe
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.fieldName;
        return newErrors;
      });
    }
  };

  // Obtener la configuración del campo
  const fieldConfig = useMemo(() => {
    if (!type) return null;
    return getFieldConfiguration(type);
  }, [type]);

  const generateId = useCallback((): string => generateFieldId(), []);

  const buildInitialDynamicValues = useCallback(
    (initialField: EntityField): Record<string, unknown> =>
      buildInitialDynamicValuesImpl(initialField, fieldConfig),
    [fieldConfig],
  );

  const setupDatacenterFields = useCallback(
    (
      initialField: EntityField,
      dynamicValues: Record<string, unknown>,
    ): void => {
      setupDatacenterFieldsImpl(initialField, dynamicValues, fieldConfig);
    },
    [fieldConfig],
  );

  const setupFileTypeConfiguration = useCallback(
    (
      initialField: EntityField,
      dynamicValues: Record<string, unknown>,
    ): void => {
      setupFileTypeConfigurationImpl(initialField, dynamicValues);
    },
    [],
  );

  const setupOptionsRootConfiguration = useCallback(
    (
      initialField: EntityField,
      dynamicValues: Record<string, unknown>,
    ): void => {
      setupOptionsRootConfigurationImpl(
        initialField,
        dynamicValues,
        fieldConfig,
      );
    },
    [fieldConfig],
  );

  const setupDropdownButtonConfiguration = useCallback(
    (
      initialField: EntityField,
      dynamicValues: Record<string, unknown>,
    ): void => {
      setupDropdownButtonConfigurationImpl(
        initialField,
        dynamicValues,
        fieldConfig,
        setLocalListItems,
      );
    },
    [fieldConfig],
  );

  const setupIdentificationField = useCallback(
    (
      initialField: EntityField,
      dynamicValues: Record<string, unknown>,
    ): void => {
      setupIdentificationFieldImpl(initialField, dynamicValues, type);
    },
    [type],
  );

  const setupSourceConfiguration = useCallback(
    (
      initialField: EntityField,
      dynamicValues: Record<string, unknown>,
    ): void => {
      setupSourceConfigurationImpl(initialField, dynamicValues);
    },
    [],
  );

  /**
   * Used to reset the form values to default when creating a new field or when the modal is opened without an initial field.
   * This function sets the field ID, name, key, and form values to their default states.
   * It also clears the local list items, sets the isArray flag to false, and resets the allowed quantity of values.
   * The function is memoized using useCallback to avoid unnecessary re-renders.
   * It depends on the fieldConfig and generateId functions.
   */
  const setEmptyFormValues = useCallback(() => {
    // Modo creación: usar valores por defecto
    setFieldId(generateId());
    setFieldName("");
    setKeyValue("");

    const defaultValues: Record<string, unknown> = {};
    fieldConfig?.sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.key !== "fieldName") {
          defaultValues[field.key] = field.defaultValue;
        }
      });
    });
    setFormValues(defaultValues);
    setLocalListItems([]);
    setIsArray(false);
    setAllowedQuantityOfValues("");
  }, [fieldConfig, generateId]);

  /**
   * Sets the form values based on the initial field provided. This function is used when editing an existing field to populate the form with its current values.
   * It orchestrates multiple setup functions to configure all aspects of the field.
   * The function is memoized using useCallback to avoid unnecessary re-renders.
   * It depends on the initialField, fieldConfig, type, and generateId functions.
   */
  const setFormValuesFromInitialField = useCallback(() => {
    if (!initialField || !fieldConfig) return;

    // Set basic field information
    setFieldId(initialField.id || generateId());
    setFieldName(initialField.name || "");
    setKeyValue(
      initialField.key || initialField.name
        ? parseCharacters(initialField.name.toLowerCase())
        : "",
    );

    // Build and configure all dynamic values
    const dynamicValues = buildInitialDynamicValues(initialField);
    setupFileTypeConfiguration(initialField, dynamicValues);
    setupDatacenterFields(initialField, dynamicValues);
    setupOptionsRootConfiguration(initialField, dynamicValues);
    setupDropdownButtonConfiguration(initialField, dynamicValues);
    setupIdentificationField(initialField, dynamicValues);
    setupSourceConfiguration(initialField, dynamicValues);

    // Apply all values at once
    setFormValues(dynamicValues);
    setIsArray(initialField.is_array || false);
    setAllowedQuantityOfValues(initialField.max_items?.toString() || "");
  }, [
    initialField,
    fieldConfig,
    generateId,
    buildInitialDynamicValues,
    setupFileTypeConfiguration,
    setupDatacenterFields,
    setupOptionsRootConfiguration,
    setupDropdownButtonConfiguration,
    setupIdentificationField,
    setupSourceConfiguration,
  ]);

  // Inicializar valores cuando se abre el modal o cambia la configuración
  useEffect(() => {
    if (isOpen && fieldConfig) {
      if (initialField) {
        setFormValuesFromInitialField();
      } else {
        setEmptyFormValues();
      }

      setLocked(true);
      setNewListItem("");
      setNewFileType("");
      setErrors({});
    }
  }, [
    isOpen,
    initialField,
    fieldConfig,
    setFormValuesFromInitialField,
    setEmptyFormValues,
  ]);

  // Función para actualizar valores del formulario
  const updateFormValue = useCallback((key: string, value: unknown) => {
    setFormValues((prev) => {
      const updated = { ...prev, [key]: value };

      // Reset defaultValue when options_root_source changes
      if (key === "options_root_source" && prev.options_root_source !== value) {
        updated.defaultValue = "";
      }

      return updated;
    });

    // Limpiar error si existe
    setErrors((prevErrors) => {
      if (prevErrors[key]) {
        const newErrors = { ...prevErrors };
        delete newErrors[key];
        return newErrors;
      }
      return prevErrors;
    });
  }, []);

  const hasRequiredValidationError = useCallback(
    (field: FieldConfig, value: unknown): boolean =>
      hasRequiredValidationErrorImpl(field, value, formValues, localListItems),
    [formValues, localListItems],
  );

  const manageFieldValidation = useCallback(
    (field: FieldConfig, newErrors: Record<string, string>) => {
      manageFieldValidationImpl(field, newErrors, formValues, localListItems);
    },
    [formValues, localListItems],
  );

  // Función para validar el formulario
  const validate = (): boolean => {
    if (!fieldConfig) return false;

    const newErrors: Record<string, string> = {};

    // Validar fieldName (siempre requerido)
    if (!fieldName.trim()) {
      newErrors.fieldName = "El nombre del campo es requerido";
    } else if (fieldName.trim().toUpperCase() === "ID") {
      newErrors.fieldName = "No se puede crear un campo con el nombre 'ID'";
    }

    // Validar campos según la configuración
    fieldConfig.sections.forEach((section) => {
      section.fields.forEach((field) =>
        manageFieldValidation(field, newErrors),
      );
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Función para obtener los datos del campo listos para guardar
  const getFieldData = (): EntityField | null =>
    buildFieldData({
      type,
      fieldName,
      fieldConfig,
      formValues,
      fieldId,
      generateId,
      keyValue,
      isArray,
      allowedQuantityOfValues,
      localListItems,
    });

  // Función para actualizar valores de centro de datos
  const setDataCenterValue = useCallback(
    (fieldKey: string, tableName: string, columnName: string) => {
      setFormValues((prev) => ({
        ...prev,
        [`${fieldKey}_table`]: tableName,
        [`${fieldKey}_column`]: columnName,
      }));
    },
    [],
  );

  // Función para resetear el formulario
  const reset = () => {
    setFieldId("");
    setFieldNameState("");
    setKeyValue("");
    setFormValues({});
    setLocalListItems([]);
    setNewListItem("");
    setNewFileType("");
    setLocked(true);
    setErrors({});
  };

  // Calcular si el formulario es válido
  const isValid = useMemo(() => {
    if (!fieldConfig || !fieldName.trim()) {
      return false;
    }

    // Verificar que no haya errores
    if (Object.keys(errors).length > 0) {
      return false;
    }

    // Verificar campos requeridos visibles
    let hasAllRequired = true;

    fieldConfig.sections.forEach((section) => {
      section.fields.forEach((field) => {
        // fieldName se maneja por separado, ya lo validamos arriba
        if (field.key === "fieldName") {
          return;
        }

        const isVisible = !field.condition || field.condition(formValues);
        if (!isVisible || !field.required) {
          return;
        }

        if (hasRequiredValidationError(field, formValues[field.key])) {
          hasAllRequired = false;
        }
      });
    });
    return hasAllRequired;
  }, [fieldConfig, fieldName, formValues, errors, hasRequiredValidationError]);

  return {
    // Valores
    fieldName,
    keyValue,
    formValues,
    localListItems,
    newListItem,
    newFileType,
    locked,
    isArray,
    allowedQuantityOfValues,

    // Estado de validación
    errors,
    isValid,
    isNew: initialField ? fieldId?.includes("field_") : true,

    // Funciones de actualización
    setFieldName,
    setKeyValue,
    updateFormValue,
    setLocalListItems,
    setNewListItem,
    setNewFileType,
    setLocked,
    setIsArray,
    setAllowedQuantityOfValues,
    setDataCenterValue,
    // Funciones de utilidad
    reset,
    getFieldData,
    validate,

    // Configuración
    fieldConfig: fieldConfig || null,
  };
};
